import { forwardRef, useState } from "react";
import * as RPNInput from "react-phone-number-input";

import {
  PhoneInputControl,
  PhoneInputSelect,
  type PhoneInputControlProps,
} from "./phone-input.components";
import {
  phoneInputRootVariants,
  type PhoneInputRootVariants,
} from "./phone-input.styles";
import { formatAriaInvalid } from "./style.utils";

type RPNInputDefault = typeof RPNInput.default;
type RPNInputDefaultProps = RPNInput.Props<RPNInputDefault>;
type RPNInputValue = RPNInput.Value;
type RPNInputValueOptional = RPNInputValue | undefined;

export type PhoneInputRef = React.ComponentRef<RPNInputDefault>;

export interface PhoneInputProps
  extends
    Omit<PhoneInputControlProps, "defaultValue" | "onChange" | "value">,
    Omit<
      RPNInputDefaultProps,
      "autoComplete" | "onBlur" | "onChange" | "onFocus" | "style"
    >,
    PhoneInputRootVariants {
  defaultValue?: RPNInputValueOptional;
  onChange?: (value: RPNInputValue) => void;
}

/**
 * A custom phone input component built on top of `react-phone-number-input`
 * inpired by the design of `shadcn-phone-input`.
 * @see https://www.npmjs.com/package/react-phone-number-input
 * @see https://github.com/omeralpi/shadcn-phone-input
 */
const PhoneInput = forwardRef<PhoneInputRef, PhoneInputProps>(
  (
    {
      "aria-invalid": ariaInvalid,
      className,
      defaultValue,
      disabled,
      onChange,
      placeholder,
      size,
      value,
      ...props
    },
    ref,
  ) => {
    const valueOrDefault: RPNInputValueOptional = value ?? defaultValue;

    const [countryCode, setCountryCode] = useState("US");
    const invalid = formatAriaInvalid(ariaInvalid);

    return (
      <RPNInput.default
        smartCaret={false}
        defaultCountry="US"
        countryOptionsOrder={["US", "CA", "..."]}
        countrySelectComponent={(componentProps) => (
          <PhoneInputSelect
            {...componentProps}
            onChange={(country) => {
              setCountryCode(country); // Update placeholder state
              componentProps.onChange?.(country);
            }}
            size={size}
            invalid={invalid}
            disabled={disabled}
          />
        )}
        inputComponent={PhoneInputControl}
        {...props}
        placeholder={
          // Empty or full US values use the default US phone format.
          // Otherwise, use the provided placeholder.
          (!valueOrDefault && countryCode === "US") || countryCode === "US"
            ? (placeholder ?? "(xxx) xxx-xxxx")
            : placeholder
        }
        value={valueOrDefault || undefined}
        onChange={(nextValue) => {
          /**
           * The onChange event might trigger as `undefined`
           * when a valid phone number is not entered.
           * To prevent this, the value is coerced to an empty string.
           */
          onChange?.(nextValue || ("" as RPNInputValue));
        }}
        disabled={disabled}
        aria-invalid={ariaInvalid}
        size={size}
        className={phoneInputRootVariants({ className, disabled, invalid })}
        ref={ref}
      />
    );
  },
);

PhoneInput.displayName = "PhoneInput";

export { PhoneInput };
